import { router } from '@inertiajs/react'
import { ArrowLeftIcon, PhoneIcon, ClockIcon, UserIcon, TruckIcon, DollarSignIcon, AlertCircleIcon, CheckCircle2Icon, Volume2Icon, PlayIcon, PauseIcon } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface Call {
  id: number
  direction: string | null
  from_number: string | null
  to_number: string | null
  employee_name: string | null
  time_chicago: string
  filename: string
  duration: number | null
  duration_formatted: string
  department: string | null
  call_category: string | null
  call_type: string | null
  caller_type: string | null
  load_source: string | null
  load_type: string | null
  broker_name: string | null
  lane: string | null
  rate_per_mile: number | null
  total_rate: number | null
  load_status: string | null
  repair_type: string | null
  truck_id: string | null
  repair_status: string | null
  repair_estimate: number | null
  parts_needed: string | null
  eta_completion: string | null
  revenue_discussed: boolean | null
  weekly_gross: number | null
  dispatch_fee: number | null
  competitor_mentioned: boolean | null
  objection: string | null
  deal_status: string | null
  action_promised: string | null
  action_deadline: string | null
  callback_required: boolean | null
  callback_time: string | null
  documents_to_send: string | null
  escalation_needed: boolean | null
  professionalism_score: number | null
  call_handled_well: boolean | null
  missed_opportunity: boolean | null
  caller_sentiment: string | null
  issue_resolved: boolean | null
  topic: string | null
  transcript_language: string | null
  raw_transcript: string | null
  audio_url: string | null
}

interface CallShowProps {
  call: Call
}

export default function CallShow({ call }: CallShowProps) {
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [audioError, setAudioError] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const updateTime = () => setCurrentTime(audio.currentTime)
    const updateDuration = () => setDuration(audio.duration)
    const handleEnded = () => setIsPlaying(false)
    const handleError = () => {
      setAudioError(true)
      setIsPlaying(false)
    }

    audio.addEventListener('timeupdate', updateTime)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('timeupdate', updateTime)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play().catch(() => setAudioError(true))
    }
    setIsPlaying(!isPlaying)
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return

    const time = parseFloat(e.target.value)
    audio.currentTime = time
    setCurrentTime(time)
  }

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getSentimentBadge = (sentiment: string | null) => {
    if (!sentiment) return 'secondary'
    switch (sentiment) {
      case 'Happy':
        return 'default'
      case 'Frustrated':
      case 'Angry':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  const getDepartmentColor = (department: string | null) => {
    if (!department) return 'secondary'
    switch (department) {
      case 'Dispatch':
        return 'default'
      case 'Fleet & Maintenance':
        return 'destructive'
      case 'HR & Recruiting':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            {/* Header with Back Button */}
            <div className="mb-6 flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => router.visit('/calls')}
                data-testid="back-to-calls-button"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back to Calls
              </Button>
            </div>

            {/* Page Title */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold tracking-tight">Call Details</h1>
              <p className="text-muted-foreground">
                {call.filename}
              </p>
            </div>

            {/* Main Grid */}
            <div className="grid gap-6">
              {/* Call Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PhoneIcon className="h-5 w-5" />
                    Call Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Time (CT)</p>
                      <p className="mt-1 font-medium">{call.time_chicago}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Duration</p>
                      <p className="mt-1 font-medium">{call.duration_formatted}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Employee</p>
                      <p className="mt-1 font-medium">{call.employee_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">From Number</p>
                      <p className="mt-1 font-medium">{call.from_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">To Number</p>
                      <p className="mt-1 font-medium">{call.to_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Direction</p>
                      <p className="mt-1 font-medium">{call.direction || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Department</p>
                      <div className="mt-1">
                        {call.department ? (
                          <Badge variant={getDepartmentColor(call.department)}>
                            {call.department}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Call Type</p>
                      <div className="mt-1">
                        {call.call_type ? (
                          <Badge variant="outline">{call.call_type}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Caller Type</p>
                      <div className="mt-1">
                        {call.caller_type ? (
                          <Badge variant="outline">{call.caller_type}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Caller Context Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserIcon className="h-5 w-5" />
                    Caller Context
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Sentiment</p>
                      <div className="mt-1">
                        {call.caller_sentiment ? (
                          <Badge variant={getSentimentBadge(call.caller_sentiment)}>
                            {call.caller_sentiment}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Issue Resolved</p>
                      <div className="mt-1">
                        {call.issue_resolved === true ? (
                          <Badge variant="default">
                            <CheckCircle2Icon className="mr-1 h-3 w-3" />
                            Yes
                          </Badge>
                        ) : call.issue_resolved === false ? (
                          <Badge variant="destructive">No</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Language</p>
                      <p className="mt-1">{call.transcript_language || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Dispatch Details Card - Only show if department is Dispatch */}
              {call.department === 'Dispatch' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TruckIcon className="h-5 w-5" />
                      Dispatch Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Load Source</p>
                        <p className="mt-1">{call.load_source || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Load Type</p>
                        <p className="mt-1">{call.load_type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Load Status</p>
                        <p className="mt-1">{call.load_status || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Broker Name</p>
                        <p className="mt-1 font-medium">{call.broker_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Lane</p>
                        <p className="mt-1">{call.lane || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Rate per Mile</p>
                        <p className="mt-1 font-medium">
                          {call.rate_per_mile ? `$${call.rate_per_mile.toFixed(2)}` : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total Rate</p>
                        <p className="mt-1 font-medium text-lg">
                          {call.total_rate ? `$${call.total_rate.toFixed(2)}` : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Weekly Gross</p>
                        <p className="mt-1 font-medium">
                          {call.weekly_gross ? `$${call.weekly_gross.toFixed(2)}` : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Dispatch Fee</p>
                        <p className="mt-1">
                          {call.dispatch_fee ? `$${call.dispatch_fee.toFixed(2)}` : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Revenue Discussed</p>
                        <p className="mt-1">
                          {call.revenue_discussed === null ? '-' : call.revenue_discussed ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Fleet & Repair Card - Only show if department is Fleet & Maintenance */}
              {call.department === 'Fleet & Maintenance' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TruckIcon className="h-5 w-5" />
                      Fleet & Repair Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Repair Type</p>
                        <p className="mt-1 font-medium">{call.repair_type || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Truck ID</p>
                        <p className="mt-1 font-medium">{call.truck_id || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Repair Status</p>
                        <p className="mt-1">{call.repair_status || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Repair Estimate</p>
                        <p className="mt-1 font-medium text-lg">
                          {call.repair_estimate ? `$${call.repair_estimate.toFixed(2)}` : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">ETA Completion</p>
                        <p className="mt-1">{call.eta_completion || '-'}</p>
                      </div>
                      <div className="md:col-span-2 lg:col-span-3">
                        <p className="text-sm font-medium text-muted-foreground">Parts Needed</p>
                        <p className="mt-1 whitespace-pre-wrap">{call.parts_needed || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Business Context Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSignIcon className="h-5 w-5" />
                    Business Context
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Deal Status</p>
                      <p className="mt-1">{call.deal_status || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Competitor Mentioned</p>
                      <p className="mt-1">
                        {call.competitor_mentioned === null ? '-' : call.competitor_mentioned ? 'Yes' : 'No'}
                      </p>
                    </div>
                    {call.objection && (
                      <div className="md:col-span-2">
                        <p className="text-sm font-medium text-muted-foreground">Objection</p>
                        <p className="mt-1 whitespace-pre-wrap">{call.objection}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Action Items Card */}
              {(call.action_promised || call.callback_required || call.documents_to_send || call.escalation_needed) && (
                <Card className={call.escalation_needed ? 'border-destructive' : ''}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircleIcon className={`h-5 w-5 ${call.escalation_needed ? 'text-destructive' : ''}`} />
                      Action Items
                      {call.escalation_needed && (
                        <Badge variant="destructive" className="ml-2">Escalation Needed</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      {call.callback_required && (
                        <div className="md:col-span-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="destructive">Callback Required</Badge>
                            {call.callback_time && (
                              <span className="text-sm text-muted-foreground">
                                Due: {call.callback_time}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {call.action_promised && (
                        <div className="md:col-span-2">
                          <p className="text-sm font-medium text-muted-foreground">Action Promised</p>
                          <p className="mt-1 whitespace-pre-wrap">{call.action_promised}</p>
                          {call.action_deadline && (
                            <p className="mt-1 text-sm text-muted-foreground">
                              Deadline: {call.action_deadline}
                            </p>
                          )}
                        </div>
                      )}
                      {call.documents_to_send && (
                        <div className="md:col-span-2">
                          <p className="text-sm font-medium text-muted-foreground">Documents to Send</p>
                          <p className="mt-1 whitespace-pre-wrap">{call.documents_to_send}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Performance Metrics Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClockIcon className="h-5 w-5" />
                    Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Professionalism Score</p>
                      <p className="mt-1 text-3xl font-bold">
                        {call.professionalism_score !== null ? (
                          <>
                            {call.professionalism_score}
                            <span className="text-base font-normal text-muted-foreground"> / 10</span>
                          </>
                        ) : (
                          <span className="text-base font-normal text-muted-foreground">-</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Call Handled Well</p>
                      <div className="mt-1">
                        {call.call_handled_well === true ? (
                          <Badge variant="default">
                            <CheckCircle2Icon className="mr-1 h-3 w-3" />
                            Yes
                          </Badge>
                        ) : call.call_handled_well === false ? (
                          <Badge variant="destructive">No</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Missed Opportunity</p>
                      <div className="mt-1">
                        {call.missed_opportunity === true ? (
                          <Badge variant="destructive">
                            <AlertCircleIcon className="mr-1 h-3 w-3" />
                            Yes
                          </Badge>
                        ) : call.missed_opportunity === false ? (
                          <Badge variant="default">No</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Conversation & Audio Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Volume2Icon className="h-5 w-5" />
                    Conversation & Audio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {call.topic && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Topic</p>
                        <p className="mt-1">{call.topic}</p>
                      </div>
                    )}

                    {call.audio_url && (
                      <div>
                        <p className="mb-2 text-sm font-medium text-muted-foreground">Call Recording</p>
                        <audio ref={audioRef} src={call.audio_url} className="hidden" />

                        {audioError ? (
                          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                            <p className="text-sm text-destructive">Unable to load audio in player.</p>
                            <a
                              href={call.audio_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline"
                            >
                              Open audio file directly →
                            </a>
                          </div>
                        ) : (
                          <div className="rounded-lg border bg-muted/30 p-4">
                            <div className="flex items-center gap-3">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={togglePlay}
                                className="h-10 w-10 shrink-0"
                              >
                                {isPlaying ? (
                                  <PauseIcon className="h-5 w-5" />
                                ) : (
                                  <PlayIcon className="h-5 w-5" />
                                )}
                              </Button>

                              <div className="flex-1">
                                <input
                                  type="range"
                                  min="0"
                                  max={duration || 0}
                                  value={currentTime}
                                  onChange={handleSeek}
                                  className="w-full cursor-pointer"
                                  style={{
                                    background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${(currentTime / duration) * 100}%, hsl(var(--muted)) ${(currentTime / duration) * 100}%, hsl(var(--muted)) 100%)`
                                  }}
                                />
                                <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                                  <span>{formatTime(currentTime)}</span>
                                  <span>{formatTime(duration)}</span>
                                </div>
                              </div>
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground">
                              Can't hear? Try{' '}
                              <a
                                href={call.audio_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                              >
                                opening directly
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {call.raw_transcript && (
                      <div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-muted-foreground">Raw Transcript</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setTranscriptOpen(!transcriptOpen)}
                          >
                            {transcriptOpen ? 'Hide' : 'Show'} Transcript
                          </Button>
                        </div>
                        {transcriptOpen && (
                          <div className="mt-2">
                            <div className="rounded-md bg-muted p-4 max-h-96 overflow-y-auto">
                              <p className="whitespace-pre-wrap text-sm font-mono">
                                {call.raw_transcript}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
